import type { Direction, Transaction } from "./finance.ts";
import { brandContext } from "./business-units.ts";
import type { FinanceCategory, CategoryKind } from "./categories.ts";

export type FinancialSection =
  | "revenue"
  | "cogs"
  | "operating_expense"
  | "non_operating_income"
  | "non_operating_expense"
  | "excluded";

export type FinancialAccount = {
  slug: string;
  nameId: string;
  nameEn: string;
  section: FinancialSection;
  displayOrder: number;
  active: boolean;
  system: boolean;
};

export type FinancialCodeRule = {
  uniqueCode: string;
  direction: Direction;
  financialAccount: string;
  businessUnit?: string | null;
  createdBy?: string | null;
  createdAt?: string;
  updatedAt?: string;
};

export type CategoryFinancialMapping = { category: string; financialAccount: string; active: boolean; updatedAt?: string };
export type MappingSource = "manual" | "rule" | "category" | "subcategory" | "legacy" | "unmapped";
export type FinancialResolutionReason = 'resolved' | 'manual_empty' | 'missing_account' | 'inactive_account' | 'missing_mapping' | 'uncategorized' | 'invalid_category' | 'invalid_mapping';

export type ResolvedFinancialAccount = {
  account: FinancialAccount | null;
  source: MappingSource;
  reason: FinancialResolutionReason;
  mappingCategory?: string;
  accountSlug?: string;
};

export type AccountTotal = {
  account: FinancialAccount;
  amount: number;
  transactionCount: number;
};

export type BrandProfitLoss = {
  businessUnit: string;
  revenue: number;
  cogs: number;
  grossProfit: number;
  operatingExpense: number;
  operatingProfit: number;
  nonOperatingIncome: number;
  nonOperatingExpense: number;
  netProfit: number;
  income: number;
  expense: number;
  transactionCount: number;
};

export type ProfitLossReport = BrandProfitLoss & {
  accountTotals: AccountTotal[];
  byBrand: BrandProfitLoss[];
  byCollaboration: BrandProfitLoss[];
  unmappedCount: number;
  unmappedAmount: number;
  excludedCount: number;
  eligibleCount: number;
};

/** v0.12 has no assumed chart of accounts. Owner creates the actual structure. */
export const defaultFinancialAccounts: FinancialAccount[] = [];

export const financialSectionLabels: Record<FinancialSection, { id: string; en: string }> = {
  revenue: { id: "Pendapatan", en: "Revenue" },
  cogs: { id: "Harga Pokok Penjualan", en: "Cost of Goods Sold" },
  operating_expense: { id: "Beban Operasional", en: "Operating Expenses" },
  non_operating_income: { id: "Pendapatan di Luar Usaha", en: "Non-operating Income" },
  non_operating_expense: { id: "Beban di Luar Usaha", en: "Non-operating Expense" },
  excluded: { id: "Dikecualikan dari Laporan", en: "Excluded from Report" },
};

export function resolveFinancialAccount(
  transaction: Transaction,
  accounts: FinancialAccount[] = defaultFinancialAccounts,
  rules: FinancialCodeRule[] = [],
  mappings: CategoryFinancialMapping[] = [],
  categories: FinanceCategory[] = [],
): ResolvedFinancialAccount {
  return createFinancialResolver(accounts, rules, mappings, categories)(transaction);
}

export function financialSectionMatchesCategory(kind: CategoryKind, section: FinancialSection) {
  return section === 'excluded' || (kind === 'income' ? ['revenue','non_operating_income'].includes(section) : ['cogs','operating_expense','non_operating_expense'].includes(section));
}

/** One reusable index per configuration. Raw transaction assignments are never changed. */
export function createFinancialResolver(accounts: FinancialAccount[], rules: FinancialCodeRule[] = [], mappings: CategoryFinancialMapping[] = [], categories: FinanceCategory[] = []) {
  const accountBySlug = new Map(accounts.map((a) => [a.slug,a]));
  const categoryBySlug = new Map(categories.map((c) => [c.slug,c]));
  const mappingByCategory = new Map(mappings.filter((m) => m.active).map((m) => [m.category,m]));
  const codeByKey = new Map(rules.map((r) => [`${r.direction}:${r.uniqueCode}`,r]));
  const cache = new WeakMap<Transaction,ResolvedFinancialAccount>();
  const resolveAccount = (slug: string, source: MappingSource, mappingCategory?: string): ResolvedFinancialAccount => {
    const account=accountBySlug.get(slug) || null;
    return {account,source,mappingCategory,accountSlug:slug,reason:!account?'missing_account':!account.active?'inactive_account':'resolved'};
  };
  return (row: Transaction): ResolvedFinancialAccount => {
    const cached=cache.get(row);if(cached)return cached;
    let result: ResolvedFinancialAccount;
    const stored=row.financialAccount;
    if(row.financialMappingSource==='manual') result=stored?resolveAccount(stored,'manual'):{account:null,source:'manual',reason:'manual_empty'};
    else if(stored && !['rule','category'].includes(row.financialMappingSource || '')) result=resolveAccount(stored,'legacy');
    else {
      const parent=categoryBySlug.get(row.category);
      const sub=row.subcategory?categoryBySlug.get(row.subcategory):undefined;
      const validParent=parent && !parent.system && !parent.parentSlug && parent.kind===(row.direction==='CREDIT'?'income':'cost');
      const subMap=validParent && sub?.parentSlug===parent.slug && sub.kind===parent.kind?mappingByCategory.get(sub.slug):undefined;
      const parentMap=validParent?mappingByCategory.get(parent.slug):undefined;
      const mapping=subMap || parentMap;
      if(mapping) {
        result=resolveAccount(mapping.financialAccount,subMap?'subcategory':'category',mapping.category);
        if(result.account && !financialSectionMatchesCategory(parent!.kind,result.account.section))result={...result,account:null,reason:'invalid_mapping'};
      } else if(stored) result=resolveAccount(stored,row.financialMappingSource==='rule'?'rule':'category');
      else {
        const rule=codeByKey.get(`${row.direction}:${row.uniqueCode}`);
        result=rule?resolveAccount(rule.financialAccount,'rule'):{account:null,source:'unmapped',reason:row.category==='uncategorized'?'uncategorized':!validParent&&categories.length?'invalid_category':'missing_mapping'};
      }
    }
    cache.set(row,result);return result;
  };
}

export function financialSourceLabel(source: MappingSource, language: 'id'|'en') {
  const labels:Record<MappingSource,[string,string]>={manual:['Pilihan manual Owner','Owner manual choice'],legacy:['Penetapan lama dipertahankan','Preserved legacy assignment'],rule:['Aturan kode tersimpan','Recorded code rule'],category:['Pemetaan kategori','Category mapping'],subcategory:['Pemetaan subkategori','Subcategory mapping'],unmapped:['Belum dipetakan','Unmapped']};
  return labels[source][language==='id'?0:1];
}
export function financialReasonLabel(reason: FinancialResolutionReason, language: 'id'|'en') {
  const labels:Record<FinancialResolutionReason,[string,string]>={resolved:['Sudah dipetakan','Mapped'],manual_empty:['Akun sengaja dikosongkan Owner','Account intentionally cleared by Owner'],missing_account:['Akun tersimpan tidak ditemukan','Stored account unavailable'],inactive_account:['Akun laporan nonaktif','Report account inactive'],missing_mapping:['Kategori belum memiliki pemetaan akun','Category has no account mapping'],uncategorized:['Transaksi belum dikategorikan','Transaction uncategorized'],invalid_category:['Kategori tidak sesuai / tidak ditemukan','Category mismatched or unavailable'],invalid_mapping:['Pemetaan tidak sesuai jenis kategori','Mapping mismatches category type']};
  return labels[reason][language==='id'?0:1];
}

function signedAmount(transaction: Transaction, section: FinancialSection) {
  if (section === "excluded") return 0;
  const incomeSection = section === "revenue" || section === "non_operating_income";
  const naturalDirection: Direction = incomeSection ? "CREDIT" : "DEBIT";
  return transaction.direction === naturalDirection ? transaction.amount : -transaction.amount;
}

function emptyBrand(businessUnit: string): BrandProfitLoss {
  return {
    businessUnit,
    revenue: 0,
    cogs: 0,
    grossProfit: 0,
    operatingExpense: 0,
    operatingProfit: 0,
    nonOperatingIncome: 0,
    nonOperatingExpense: 0,
    netProfit: 0,
    income: 0,
    expense: 0,
    transactionCount: 0,
  };
}

function applyAmount(summary: BrandProfitLoss, section: FinancialSection, amount: number) {
  if (section === "revenue") summary.revenue += amount;
  if (section === "cogs") summary.cogs += amount;
  if (section === "operating_expense") summary.operatingExpense += amount;
  if (section === "non_operating_income") summary.nonOperatingIncome += amount;
  if (section === "non_operating_expense") summary.nonOperatingExpense += amount;
  if (section !== "excluded") summary.transactionCount += 1;
}

function finalize(summary: BrandProfitLoss) {
  summary.grossProfit = summary.revenue - summary.cogs;
  summary.operatingProfit = summary.grossProfit - summary.operatingExpense;
  summary.netProfit = summary.operatingProfit + summary.nonOperatingIncome - summary.nonOperatingExpense;
  summary.income = summary.revenue + summary.nonOperatingIncome;
  summary.expense = summary.cogs + summary.operatingExpense + summary.nonOperatingExpense;
  return summary;
}

/**
 * Cash-basis report. It never mutates transactions and de-duplicates repeated
 * source links by transaction id before calculating totals.
 */
export function calculateProfitLoss(
  transactions: Transaction[],
  accounts: FinancialAccount[] = defaultFinancialAccounts,
  rules: FinancialCodeRule[] = [],
  mappings: CategoryFinancialMapping[] = [],
  categories: FinanceCategory[] = [],
): ProfitLossReport {
  const total = emptyBrand("all");
  const brandMap = new Map<string, BrandProfitLoss>();
  const accountMap = new Map<string, AccountTotal>();
  const seen = new Set<string>();
  let unmappedCount = 0;
  let unmappedAmount = 0;
  let excludedCount = 0;
  const resolve = createFinancialResolver(accounts,rules,mappings,categories);

  for (const transaction of transactions) {
    if (transaction.deletedAt || !transaction.direction || seen.has(transaction.id)) continue;
    seen.add(transaction.id);
    const resolved = resolve(transaction);
    if (!resolved.account || !resolved.account.active) {
      unmappedCount += 1;
      unmappedAmount += transaction.amount;
      continue;
    }
    const account = resolved.account;
    if (account.section === "excluded") {
      excludedCount += 1;
      continue;
    }
    const amount = signedAmount(transaction, account.section);
    applyAmount(total, account.section, amount);
    const context = brandContext(transaction);
    const brand = brandMap.get(context) || emptyBrand(context);
    applyAmount(brand, account.section, amount);
    brandMap.set(context, brand);
    const accountTotal = accountMap.get(account.slug) || { account, amount: 0, transactionCount: 0 };
    accountTotal.amount += amount;
    accountTotal.transactionCount += 1;
    accountMap.set(account.slug, accountTotal);
  }

  const accountTotals = [...accountMap.values()].sort((a, b) => a.account.displayOrder - b.account.displayOrder);
  const groups = [...brandMap.values()].map(finalize).sort((a, b) => b.revenue - a.revenue);
  const byBrand = groups.filter((group) => !group.businessUnit.includes("+"));
  const byCollaboration = groups.filter((group) => group.businessUnit.includes("+"));
  return {
    ...finalize(total),
    accountTotals,
    byBrand,
    byCollaboration,
    unmappedCount,
    unmappedAmount,
    excludedCount,
    eligibleCount: seen.size,
  };
}

export function dailyProfitLossSeries(
  transactions: Transaction[],
  accounts: FinancialAccount[] = defaultFinancialAccounts,
  rules: FinancialCodeRule[] = [],
  mappings: CategoryFinancialMapping[] = [],
  categories: FinanceCategory[] = [],
) {
  const dates = [...new Set(transactions.map((transaction) => transaction.date))].sort();
  return dates.map((date) => {
    const report = calculateProfitLoss(transactions.filter((transaction) => transaction.date === date), accounts, rules, mappings, categories);
    return { date, income: report.income, expense: report.expense, grossProfit: report.grossProfit };
  });
}

export function accountName(account: FinancialAccount, language: "id" | "en") {
  return language === "id" ? account.nameId : account.nameEn;
}
